/**
 * Root error boundary for the SPA.
 *
 * Catches render crashes that would otherwise leave a blank page (React's
 * default is to unmount the tree). The recurring localhost white-screen is
 * Vite Fast Refresh remounting `SubscriptionProvider` against a new
 * `AuthContext` object ("useAuth must be used within an AuthProvider")
 * when a `.tsx` file exported both a Provider and a hook. Context modules
 * are now split (`*Context.ts` + `*Provider.tsx`); this boundary remains
 * so any future render crash cannot leave a white screen.
 *
 * Auto-reload recovery (cooldown-guarded):
 * - Vite `npm run dev`: Fast Refresh context-identity mismatch
 * - Deployed SPA: stale hashed chunk after a Static Web App publish
 *   (`Failed to fetch dynamically imported module`, etc.)
 *
 * Other errors show a reload prompt. This boundary sits outside
 * `MantineProvider`, so the fallback is plain HTML.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  reloadForStaleDeployChunk,
  shouldAutoReloadForStaleDeployChunk,
} from '../../utils/staleDeployChunkReload';

/** Matches React context hooks that throw when used outside their Provider. */
export const CONTEXT_PROVIDER_ERROR_PATTERN = /must be used within an? \w+Provider/;

/** sessionStorage key: timestamp of the last auto-reload for a context HMR crash. */
const HMR_CONTEXT_RELOAD_AT_KEY = 'ss.dev.hmr-context-reload-at';

/** Ignore a second auto-reload inside this window so a real provider bug cannot loop. */
const HMR_CONTEXT_RELOAD_COOLDOWN_MS = 10_000;

/** Why the boundary is auto-reloading (drives the interim message). */
type AutoReloadReason = 'hmr-context' | 'stale-deploy-chunk' | null;

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  autoReloadReason: AutoReloadReason;
}

/**
 * Returns true when this crash is the Fast Refresh context-identity mismatch
 * and we have not already auto-reloaded within the cooldown window.
 */
function shouldAutoReloadForContextHmr(error: Error): boolean {
  // Vite `npm run dev` only. Vitest also sets `import.meta.hot`, so MODE
  // is the reliable signal that we are in the browser HMR session.
  if (!import.meta.env.DEV || import.meta.env.MODE !== 'development') {
    return false;
  }

  if (!CONTEXT_PROVIDER_ERROR_PATTERN.test(error.message)) {
    return false;
  }

  const lastRaw = sessionStorage.getItem(HMR_CONTEXT_RELOAD_AT_KEY);
  const lastAt = lastRaw ? Number(lastRaw) : 0;
  if (Number.isFinite(lastAt) && Date.now() - lastAt < HMR_CONTEXT_RELOAD_COOLDOWN_MS) {
    return false;
  }

  return true;
}

/**
 * Picks an auto-reload reason for a caught error, or null for the manual prompt.
 */
function resolveAutoReloadReason(error: Error): AutoReloadReason {
  if (shouldAutoReloadForContextHmr(error)) {
    return 'hmr-context';
  }

  if (shouldAutoReloadForStaleDeployChunk(error)) {
    return 'stale-deploy-chunk';
  }

  return null;
}

/**
 * Root error boundary. Class component because React requires
 * `getDerivedStateFromError` / `componentDidCatch` on a class.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, autoReloadReason: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return {
      hasError: true,
      error,
      autoReloadReason: resolveAutoReloadReason(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary] Caught error:', error, errorInfo);

    const reason = this.state.autoReloadReason;
    if (reason === 'hmr-context') {
      sessionStorage.setItem(HMR_CONTEXT_RELOAD_AT_KEY, String(Date.now()));
      window.location.reload();
      return;
    }

    if (reason === 'stale-deploy-chunk') {
      console.warn('[AppErrorBoundary] Stale deploy chunk — reloading for new assets');
      reloadForStaleDeployChunk();
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { autoReloadReason } = this.state;
    const message =
      autoReloadReason === 'hmr-context'
        ? 'Reloading after a development hot-reload glitch…'
        : autoReloadReason === 'stale-deploy-chunk'
          ? 'A new version is available. Reloading…'
          : 'Something went wrong while loading this page.';

    return (
      <div
        role="alert"
        style={{
          minHeight: 'var(--app-vh, 100vh)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '1.5rem',
          background: 'var(--ss-bg-page)',
          color: 'var(--ss-text-primary)',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{message}</p>
        {this.state.error && autoReloadReason == null && (
          <p
            style={{
              margin: 0,
              maxWidth: '36rem',
              fontSize: '0.875rem',
              color: 'var(--ss-text-secondary)',
            }}
          >
            {this.state.error.message}
          </p>
        )}
        {autoReloadReason == null && (
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              cursor: 'pointer',
              border: 'none',
              borderRadius: '8px',
              padding: '0.6rem 1.25rem',
              background: 'var(--ss-primary)',
              color: 'white',
              fontSize: '0.9375rem',
              fontWeight: 600,
            }}
          >
            Reload page
          </button>
        )}
      </div>
    );
  }
}
